import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material'

import LinkButtonTooltip from '../../../ui/components/LinkButtonTooltip'
import useTranslations from '../../../i18n/useTranslations'
import useIsMobile from '../../../hooks/useIsMobile'
import Letter from '../../../assets/animations/letter'
import { ReactComponent as X } from '../../../assets/icons/x.svg'
import { ReactComponent as Copy } from '../../../assets/icons/copy.svg'
import { ReactComponent as Printer } from '../../../assets/icons/printer.svg'
import { ReactComponent as DownloadSimple } from '../../../assets/icons/download-simple.svg'
import { DOWNLOADS, PRINT_SETTINGS } from '../../../routes'

import useStyles from './styles'

export const PRINTER_SERVICE_NOT_INSTALLED_ERROR = 'not-installed'
export const PRINTER_SERVICE_NOT_CONFIGURED_ERROR = 'not-configured'

const propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  shippingCode: PropTypes.string,
  createNewShipping: PropTypes.func,
  goBackHome: PropTypes.func,
  printLabel: PropTypes.func,
  downloadLabel: PropTypes.func,
  printerServiceError: PropTypes.oneOf([
    PRINTER_SERVICE_NOT_INSTALLED_ERROR,
    PRINTER_SERVICE_NOT_CONFIGURED_ERROR,
  ]),
  isOriginPudo: PropTypes.bool,
}

let notificationTimeout

const SuccessfulShipmentModalView = ({
  shippingCode,
  isOpen,
  close,
  createNewShipping,
  goBackHome,
  printerServiceError,
  printLabel,
  downloadLabel,
  isOriginPudo,
}) => {
  const t = useTranslations()
  const classes = useStyles()
  const isMobile = useIsMobile()

  const [openSuccessNotification, setOpenSuccessNotification] = useState(false)

  const triggerNotification = () => {
    if (notificationTimeout) clearTimeout(notificationTimeout)

    setOpenSuccessNotification(true)

    notificationTimeout = setTimeout(() => {
      setOpenSuccessNotification(false)
    }, 1200)
  }

  const handleCopyText = () => {
    triggerNotification()
    return navigator.clipboard.writeText(shippingCode)
  }

  useEffect(() => {
    return () => {
      if (notificationTimeout) clearTimeout(notificationTimeout)
    }
  }, [])

  return (
    <Dialog
      open={isOpen}
      disablePortal
      onClose={close}
      className={classes.dialog}
    >
      <Box className={classes.header}>
        <Letter width={400} height={200} />
        <IconButton className={classes.closeButton} onClick={close}>
          <X color="neutral.main" />
        </IconButton>
      </Box>
      <DialogContent>
        <Typography
          variant="h2"
          component="p"
          align="center"
          className={clsx('secondaryTitle')}
        >
          {t('message:successful-shipment', {
            p: text => <p>{text}</p>,
          })}
        </Typography>

        <Box className={classes.shippingCodeWrapper}>
          <Typography variant="body1" color="neutral.main">
            {t('shipping-send-success:shipping-number')}
          </Typography>

          <Tooltip
            title={
              <Typography variant="body2">{t('action:text-copied')}</Typography>
            }
            disableFocusListener
            disableHoverListener
            disableInteractive
            classes={{ tooltip: 'info' }}
            open={openSuccessNotification}
          >
            <Chip
              onClick={handleCopyText}
              label={shippingCode}
              avatar={<Copy />}
              className={classes.chip}
            />
          </Tooltip>
        </Box>

        {isMobile ? (
          <Typography
            variant="body1"
            color="neutral.main"
            className={classes.helperText}
          >
            {t('shipping-send-success:helper-text-mobile')}
          </Typography>
        ) : (
          <Typography
            variant="body1"
            color="neutral.main"
            className={classes.helperText}
          >
            {!isOriginPudo
              ? t('shipping-send-success:helper-text', {
                  strong: text => <strong>{text}</strong>,
                  p: text => <p>{text}</p>,
                })
              : t('shipping-send-success:helper-text-origin-pudo', {
                  strong: text => <strong>{text}</strong>,
                  p: text => <p>{text}</p>,
                })}
          </Typography>
        )}

        {!isMobile ? (
          printerServiceError === PRINTER_SERVICE_NOT_INSTALLED_ERROR ? (
            <Alert severity="error" className={classes.alertPrint}>
              <Typography variant="body2" component="p">
                {t('print-alert:app-not-installed')}
              </Typography>
              <Typography variant="body2" component="p">
                <Link href={DOWNLOADS.path}>
                  {t('alert:go-to-download-page')}
                </Link>
              </Typography>
            </Alert>
          ) : printerServiceError === PRINTER_SERVICE_NOT_CONFIGURED_ERROR ? (
            <Alert severity="error" className={classes.alertPrint}>
              <Typography variant="body2" component="p">
                {t('print-alert:printer-not-configured')}
              </Typography>
              <Typography variant="body2" component="p">
                <Link href={PRINT_SETTINGS.path}>
                  {t('noun:go-to-print-settings')}
                </Link>
              </Typography>
            </Alert>
          ) : null
        ) : null}

        {!isMobile ? (
          <Box className={classes.actions}>
            {!isOriginPudo && (
              <Button
                onClick={printLabel}
                startIcon={<Printer />}
                size="small"
                fullWidth
              >
                {t('action:print-shipping-label', { count: 1 })}
              </Button>
            )}

            <LinkButtonTooltip
              text={t('action:new-shipping')}
              onClick={createNewShipping}
            />
          </Box>
        ) : (
          <Box className={classes.actions}>
            <Button
              onClick={downloadLabel}
              startIcon={<DownloadSimple />}
              size="small"
              fullWidth
              className={classes.downloadLabelButton}
            >
              {t('action:download-label')}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

SuccessfulShipmentModalView.propTypes = propTypes

export default SuccessfulShipmentModalView
